import { computed, onScopeDispose, ref, Ref, watch } from 'vue';

/** «✓ Guardado» shows this long after a save. */
const SAVED_FLASH_MS = 3000;

export type TEditorExtraKey = 'translations' | 'stock';

/**
 * A part saved after the product or category itself (plan §5.12 steps
 * 3–4): translations and stock register here while their rows are open.
 */
export interface IEditorExtra {
  key: TEditorExtraKey;
  dirty: Ref<boolean>;
  /** Blocks «Guardar» while dirty (its errors show once submitted). */
  invalid?: Ref<boolean>;
  /** Saves for the entity id (the new id right after a create). */
  save: (id: number) => Promise<void>;
  reset: () => void;
}

/** «✓ Guardado» for a few seconds after a save. */
export function useSavedFlash() {
  const savedFlash = ref(false);
  let timer: number | null = null;

  function flashSaved() {
    savedFlash.value = true;
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      savedFlash.value = false;
      timer = null;
    }, SAVED_FLASH_MS);
  }

  onScopeDispose(() => {
    if (timer !== null) window.clearTimeout(timer);
  });

  return { savedFlash, flashSaved };
}

/** The editor's extras: registration, dirty count, and the saves after the entity's. */
export function useEditorExtras() {
  const extras: IEditorExtra[] = [];
  const dirtyCount = ref(0);

  function count() {
    dirtyCount.value = extras.filter((extra) => extra.dirty.value).length;
  }

  function registerExtra(extra: IEditorExtra): () => void {
    extras.push(extra);
    const stopDirty = watch(extra.dirty, count, { immediate: true });
    return () => {
      stopDirty();
      const index = extras.indexOf(extra);
      if (index > -1) extras.splice(index, 1);
      count();
    };
  }

  /** A dirty extra with errors blocks «Guardar». */
  function hasInvalid(): boolean {
    return extras.some((extra) => extra.dirty.value && extra.invalid?.value);
  }

  /** Saves the dirty extras in order; the failed keys (stops once `stale`). */
  async function saveExtras(
    id: number,
    stale: () => boolean
  ): Promise<TEditorExtraKey[]> {
    const failed: TEditorExtraKey[] = [];
    for (const extra of [...extras]) {
      if (!extra.dirty.value) continue;
      try {
        await extra.save(id);
      } catch (error) {
        console.error(`Saving ${extra.key} failed`, error);
        failed.push(extra.key);
      }
      if (stale()) return failed;
    }
    return failed;
  }

  function resetExtras() {
    for (const extra of extras) extra.reset();
  }

  return {
    dirtyCount: computed(() => dirtyCount.value),
    registerExtra,
    hasInvalid,
    saveExtras,
    resetExtras,
  };
}

/**
 * Photo picks in progress (the upload and writing its path into the form):
 * «Guardar» waits for them, so it saves what the owner sees.
 */
export function useEditorUploads() {
  const pending = new Set<Promise<void>>();

  /** Registers a pick; the task handles its own errors. */
  function track(task: Promise<void>): void {
    pending.add(task);
    const forget = () => {
      pending.delete(task);
    };
    task.then(forget, forget);
  }

  function busy(): boolean {
    return pending.size > 0;
  }

  /** Resolves once no pick is running, including those started while waiting. Never rejects. */
  async function settled(): Promise<void> {
    const ignore = () => undefined;
    while (pending.size)
      await Promise.all(
        Array.from(pending, (task) => task.then(ignore, ignore))
      );
  }

  return { track, busy, settled };
}

/** The page state the open editor mirrors (the leave guards read it). */
export interface IEditorPageFlags {
  editorDirty: boolean;
  editorName: string;
  editorGroupsSaved: boolean;
}

/** The editor that owns the page flags. */
let flagsOwner: number | null = null;
let flagsOwnerSeq = 0;

/**
 * Mirrors the editor's dirty state and name into the page state. A docked
 * editor switching in place is created before the old one is disposed: only
 * the current owner writes, so the old one's cleanup never clears the flags
 * of the new one.
 */
export function useEditorPageFlags(
  state: IEditorPageFlags,
  dirty: Readonly<Ref<boolean>>,
  name: () => string,
  groupsSaved: () => boolean = () => false
): void {
  flagsOwnerSeq += 1;
  const owner = flagsOwnerSeq;
  flagsOwner = owner;
  const owns = () => flagsOwner === owner;

  watch(
    dirty,
    (isDirty) => {
      if (owns()) state.editorDirty = isDirty;
    },
    { immediate: true }
  );
  watch(
    name,
    (value) => {
      if (owns()) state.editorName = value.trim();
    },
    { immediate: true }
  );
  watch(
    groupsSaved,
    (saved) => {
      if (owns()) state.editorGroupsSaved = saved;
    },
    { immediate: true }
  );

  onScopeDispose(() => {
    if (!owns()) return;
    flagsOwner = null;
    state.editorDirty = false;
    state.editorName = '';
    state.editorGroupsSaved = false;
  });
}
